import { useState, useEffect, useRef } from 'react';
import { PlayerScoreInfo } from './usePlayerScores';

export interface PlayerChange {
  rankDelta: number;
  scoreChanged: boolean;
}

export function useChangeDetection(playerScores: PlayerScoreInfo[]) {
  const [changeMap, setChangeMap] = useState<Map<string, PlayerChange>>(new Map());
  const prevStandingsRef = useRef<Map<string, { rank: number; scenarioScore: number }>>(new Map());
  const changeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (playerScores.length > 0) {
      const prev = prevStandingsRef.current;

      if (prev.size > 0) {
        const newChanges = new Map<string, PlayerChange>();
        playerScores.forEach((player, index) => {
          const newRank = index + 1;
          const prevData = prev.get(player.name);
          if (prevData) {
            const rankDelta = newRank - prevData.rank;
            const scoreChanged = player.scenarioScore !== prevData.scenarioScore;
            if (rankDelta !== 0 || scoreChanged) {
              newChanges.set(player.name, { rankDelta, scoreChanged });
            }
          }
        });

        if (newChanges.size > 0) {
          setChangeMap(newChanges);
          if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
          changeTimerRef.current = setTimeout(() => {
            setChangeMap(new Map());
            changeTimerRef.current = null;
          }, 2000);
        }
      }

      const newSnapshot = new Map<string, { rank: number; scenarioScore: number }>();
      playerScores.forEach((player, index) => {
        newSnapshot.set(player.name, { rank: index + 1, scenarioScore: player.scenarioScore });
      });
      prevStandingsRef.current = newSnapshot;
    } else {
      prevStandingsRef.current = new Map();
    }
  }, [playerScores]);

  useEffect(() => {
    return () => {
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
    };
  }, []);

  return { changeMap };
}
